/**
 * DetailedNutrientsPanel — two-column tables of all nutrients by category.
 * Shared between Diet Recall and Meal Plan.
 */
import * as React from "react";

import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { MealData } from "@/types/nutrition";

interface NutrientRowData {
  name: string;
  amount: number;
  unit: string;
  rda_percentage: number | null;
  has_tul?: boolean;
  exceeds_tul?: boolean;
}

const NutrientTable: React.FC<{
  title: string;
  nutrients: NutrientRowData[];
}> = ({ title, nutrients }) => {
  if (nutrients.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="bg-muted/50 border-b border-border px-3 py-2">
        <h4 className="z-label text-foreground">{title}</h4>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-border/60 hover:bg-transparent">
            <TableHead className="z-eyebrow text-muted-foreground h-7 px-3">
              Nutrient
            </TableHead>
            <TableHead className="z-eyebrow text-muted-foreground h-7 px-3 text-right">
              Amount
            </TableHead>
            <TableHead className="z-eyebrow text-muted-foreground h-7 px-3">
              RDA
            </TableHead>
            <TableHead className="z-eyebrow text-muted-foreground h-7 px-3 text-right">
              %
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {nutrients.map((nutrient) => {
            const percentage = nutrient.rda_percentage;
            const hasPercentage = percentage !== null && percentage > 0;
            const clampedPercent = hasPercentage
              ? Math.min(100, percentage)
              : 0;

            return (
              <TableRow
                key={nutrient.name}
                className="border-border/60 hover:bg-muted/20"
              >
                <TableCell className="px-3 py-1.5 z-caption text-foreground">
                  {nutrient.name}
                </TableCell>
                <TableCell className="px-3 py-1.5 z-caption text-muted-foreground text-right tabular-nums whitespace-nowrap">
                  {nutrient.amount.toFixed(1)}
                  {nutrient.unit}
                </TableCell>
                <TableCell className="px-3 py-1.5 w-24">
                  <Progress value={clampedPercent} className="h-1" />
                </TableCell>
                <TableCell className="px-3 py-1.5 z-caption text-foreground text-right tabular-nums">
                  {hasPercentage ? `${percentage.toFixed(1)}%` : "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

interface DetailedNutrientsPanelProps {
  totalNutrients: MealData["total_nutrients"] | null;
}

export const DetailedNutrientsPanel: React.FC<DetailedNutrientsPanelProps> = ({
  totalNutrients,
}) => {
  if (!totalNutrients) return null;

  const nutrients = totalNutrients as unknown as Record<
    string,
    Record<
      string,
      NutrientRowData & {
        amount: number;
        unit: string;
        rda_percentage: number | null;
      }
    >
  >;

  const convertCategoryToArray = (
    category:
      | Record<
          string,
          { amount: number; unit: string; rda_percentage: number | null }
        >
      | undefined
  ): NutrientRowData[] => {
    if (!category) return [];
    return Object.entries(category).map(([name, data]) => ({
      name,
      amount: data.amount,
      unit: data.unit,
      rda_percentage: data.rda_percentage,
    }));
  };

  const generalNutrients = convertCategoryToArray(nutrients.general);
  const carbsNutrients = convertCategoryToArray(nutrients.carbohydrates);
  const lipidsNutrients = convertCategoryToArray(nutrients.lipids);
  const proteinNutrients = convertCategoryToArray(nutrients.protein);
  const vitaminsNutrients = convertCategoryToArray(nutrients.vitamins);
  const mineralsNutrients = convertCategoryToArray(nutrients.minerals);

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-4">
        <NutrientTable title="General" nutrients={generalNutrients} />
        <NutrientTable title="Carbohydrates" nutrients={carbsNutrients} />
        <NutrientTable title="Lipids" nutrients={lipidsNutrients} />
        <NutrientTable title="Protein" nutrients={proteinNutrients} />
      </div>
      <div className="space-y-4">
        <NutrientTable title="Vitamins" nutrients={vitaminsNutrients} />
        <NutrientTable title="Minerals" nutrients={mineralsNutrients} />
      </div>
    </div>
  );
};
